import type {
  DataProvider,
  ProviderCategory,
  ProviderCoverage,
} from '@civiport/shared/providers';

import { config } from './config.js';
import { keycloak } from './keycloak.js';
import type { OrganisationKind, OrganisationNode, OrganisationsState } from './organisations.js';
import type { TenantApi } from './tenant.js';
import { currentWorkspaceId } from './workspace.js';

/**
 * Minimaler typed Fetch-Wrapper, der das aktuelle Keycloak-Token injiziert
 * und CiviPort-Fehlerantworten in eine `ApiError` umwandelt.
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly details?: unknown;

  constructor(status: number, message: string, code?: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  return request<T>(path, init);
}

/**
 * Wie `apiRequest`, aber ohne JSON: liefert die rohe Response (Binaerdaten,
 * z. B. 3D-Modelle des Zwillings). Token-Erneuerung und Header wie oben;
 * ein gesetzter Content-Type bleibt unveraendert.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  await refreshToken();
  const headers = new Headers(init.headers);
  if (keycloak.token) headers.set('Authorization', `Bearer ${keycloak.token}`);
  const workspace = currentWorkspaceId();
  if (workspace && !headers.has('X-Civiport-Workspace')) headers.set('X-Civiport-Workspace', workspace);
  return fetch(`${config.apiBaseUrl}${path}`, { ...init, headers });
}

async function refreshToken(): Promise<void> {
  if (keycloak.isTokenExpired?.(15)) {
    try {
      await keycloak.updateToken(30);
    } catch {
      // Bei Fehler: hart neu einloggen — Keycloak kuemmert sich um Redirect.
      await keycloak.login();
      throw new ApiError(401, 'token refresh failed');
    }
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  await refreshToken();

  const headers = new Headers(init.headers);
  if (keycloak.token) {
    headers.set('Authorization', `Bearer ${keycloak.token}`);
  }
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  // Gliederung: Neues gehoert ohne andere Angabe dem Arbeitsbereich (prueft der Katalog).
  const workspace = currentWorkspaceId();
  if (workspace && !headers.has('X-Civiport-Workspace')) headers.set('X-Civiport-Workspace', workspace);

  const res = await fetch(`${config.apiBaseUrl}${path}`, { ...init, headers });

  // 204 — kein Body.
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body: unknown = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    const e = body as { error?: string; message?: string; details?: unknown } | undefined;
    throw new ApiError(
      res.status,
      e?.message ?? `request failed (${res.status})`,
      e?.error,
      e?.details,
    );
  }

  return body as T;
}

// ───── Catalog ──────────────────────────────────────────────────────────

export interface SchemaColumn {
  name: string;
  type: string;
  unit?: string;
  description?: string;
  nullable?: boolean;
}

export interface SchemaDef {
  columns: SchemaColumn[];
  primary_key?: string[];
  time_field?: string;
}

export interface ContactPoint {
  fn: string;
  has_email?: string;
}

export type SpatialRef =
  | { type: 'ags' | 'ars' | 'nuts'; code: string; label?: string }
  | { type: 'geojson'; geometry: Record<string, unknown>; label?: string };

/**
 * EU-HVD-Kategorien (Durchfuehrungsverordnung 2023/138).
 * NULL = kein HVD-Dataset.
 */
export type HvdCategory =
  | 'geospatial'
  | 'earth-observation-environment'
  | 'meteorological'
  | 'statistics'
  | 'companies-company-ownership'
  | 'mobility';

export const HVD_CATEGORY_LABELS: Record<HvdCategory, string> = {
  geospatial: 'Geodaten',
  'earth-observation-environment': 'Umwelt',
  meteorological: 'Meteorologie',
  statistics: 'Statistik',
  'companies-company-ownership': 'Unternehmen',
  mobility: 'Mobilität',
};

export interface DatasetSummary {
  id: string;
  /** Eintrag der Gliederung, dem der Datensatz gehoert (null: oberste Ebene). */
  organisation_id?: string | null;
  slug: string;
  title: string;
  description: string;
  tags: string[];
  owner: string;
  lifecycle: 'active' | 'deprecated' | 'archived';
  /** `external` = kein CiviPort-Silo, nur Metadaten und Distributionen (geharvestet oder Dateien). */
  silo: 'timescale' | 'postgis' | 'relational' | 'citydb' | 'object' | 'external';
  /** Gesetzt, wenn der Eintrag aus einem fremden Katalog geharvestet ist. */
  harvest_source_id?: string | null;
  /** dct:publisher (Herausgeber). */
  publisher_id?: string | null;
  /** dct:provenance: woher die Daten stammen und wie sie entstanden sind. */
  provenance?: string | null;
  /** dct:creator: Urheber der Ausgangsdaten. */
  creator?: { name: string; homepage?: string; email?: string } | null;
  /** dct:relation: verwandte Ressourcen (z. B. der Datensatz, aus dem berechnet wurde). */
  relations?: string[];
  silo_location: string;
  schema_def: SchemaDef;
  // DCAT-AP.de Felder
  license: string | null;
  themes: string[];
  contact_point: ContactPoint | null;
  accrual_periodicity: string | null;
  spatial: SpatialRef | null;
  temporal_start: string | null;
  temporal_end: string | null;
  landing_page: string | null;
  version: string | null;
  language: string;
  access_rights: 'PUBLIC' | 'RESTRICTED' | 'NON_PUBLIC';
  conforms_to: string[];
  legal_basis: string | null;
  /** EU-HVD-Kategorie oder null wenn kein HVD-Dataset. */
  hvd_category: HvdCategory | null;
  /**
   * Aktuelle Zeilenanzahl im Silo. null fuer object/mqtt (kein Postgres-Count
   * moeglich) oder wenn die Silo-Tabelle (noch) nicht existiert. Werte aus
   * pg_class.reltuples (Schaetzung) oder COUNT(*) (Fallback). Cache 60s.
   */
  row_count?: number | null;
  /** Nur mit `include: 'distributions'` in der Liste. */
  distributions?: Distribution[];
  created_at: string;
  updated_at: string;
}

export interface Distribution {
  id: string;
  dataset_id: string;
  title: string;
  description: string | null;
  access_url: string;
  download_url: string | null;
  format: string | null;
  media_type: string | null;
  byte_size: number | null;
  /** Leer nur bei eingesammelten Distributionen, deren Quelle keine Lizenz nennt. */
  license: string | null;
  license_attribution_by_text: string | null;
  language: string;
  conforms_to: string[];
  modified: string | null;
  created_at: string;
  updated_at: string;
}

export interface DatasetListResponse {
  items: DatasetSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface DatasetListFilters {
  search?: string;
  tag?: string;
  theme?: string;
  owner?: string;
  lifecycle?: 'active' | 'deprecated' | 'archived';
  silo?: 'timescale' | 'postgis' | 'relational' | 'citydb' | 'object';
  hvd_category?: HvdCategory;
  /** Gliederung: nur dieser Eintrag (mit allem darunter, ausser subtree=false). */
  organisation?: string;
  subtree?: boolean;
  include?: 'distributions';
  sort?: 'title' | 'updated_at';
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface CreateDatasetBody {
  slug: string;
  title: string;
  description: string;
  tags?: string[];
  owner: string;
  schema_def: SchemaDef;
  silo: 'timescale' | 'postgis' | 'relational' | 'citydb' | 'object';
  silo_location: string;
  auto_create_table?: boolean;
  license?: string;
  themes?: string[];
  contact_point?: ContactPoint;
  accrual_periodicity?: string;
  spatial?: SpatialRef;
  temporal_start?: string;
  temporal_end?: string;
  landing_page?: string;
  version?: string;
  language?: string;
  access_rights?: 'PUBLIC' | 'RESTRICTED' | 'NON_PUBLIC';
  conforms_to?: string[];
  legal_basis?: string;
  hvd_category?: HvdCategory;
  /** dct:provenance: Herkunft der Daten. */
  provenance?: string;
  /** dct:creator: Urheber der Ausgangsdaten. */
  creator?: { name: string; homepage?: string; email?: string };
  /** dct:publisher; ohne Angabe die eigene Kommune. */
  publisher_id?: string;
}

export type UpdateDatasetBody = Partial<
  Omit<CreateDatasetBody, 'slug' | 'silo' | 'silo_location' | 'auto_create_table' | 'publisher_id'>
> & {
  publisher_id?: string | null;
  /** Gliederung: umziehen („Gehört zu“); eigener Herausgeber zieht mit. */
  organisation_id?: string;
  lifecycle?: 'active' | 'deprecated' | 'archived';
};

export interface CreateDistributionBody {
  title: string;
  description?: string;
  access_url: string;
  download_url?: string;
  format?: string;
  media_type?: string;
  byte_size?: number;
  license: string;
  license_attribution_by_text?: string;
  language?: string;
  conforms_to?: string[];
  modified?: string;
}

export type SourceType =
  | 'mqtt'
  | 'rest'
  | 'webhook'
  | 'file'
  | 'opcua'
  | 'modbus'
  | 'custom'
  | 'http-json'
  | 'http-csv'
  /** Basisdaten des Digitalen Zwillings, gebaut vom Arbeitsdienst twin-basedata. */
  | 'twin-basedata';

export type SourceStrategy =
  | 'scheduled_pull'
  | 'webhook_push'
  | 'streaming'
  | 'snapshot_on_demand';

export interface CreateSourceBody {
  type: SourceType;
  flow_id: string;
  strategy: SourceStrategy;
  schedule_cron?: string;
  config?: Record<string, unknown>;
}

export interface UpdateSourceBody extends Partial<CreateSourceBody> {
  status?: 'ok' | 'warning' | 'error' | 'inactive' | 'running';
}

/**
 * Ergebnis-Schema des Ingestor-Job-Triggers (POST /api/ingestor/jobs/:id/run).
 * Variabel je nach Adapter; im Fehlerfall enthaelt `error` eine Beschreibung.
 */
export interface RunSourceJobResult {
  /** `started`: lang laufende Quelle, im Hintergrund gestartet. */
  status?: 'success' | 'error' | 'skipped' | 'started';
  reason?: string;
  attempts?: number;
  rowsFetched?: number;
  rowsIngested?: number;
  durationMs?: number;
  error?: string;
}


/**
 * Smoke-Test ueber den Ingestor-Proxy (POST /api/ingestor/smoke-test).
 * Pingt eine externe URL an und gibt eine kurze Vorschau zurueck — gedacht
 * fuer den Wizard, damit der Nutzer eine URL pruefen kann, bevor er die
 * Quelle anlegt. Browser-fetch geht oft nicht (kein CORS bei DWD/UBA).
 */
export interface SmokeTestSourceBody {
  url: string;
  method?: 'GET' | 'POST';
  headers?: Record<string, string>;
  body?: string;
  /**
   * MR 6/6 (Wizard-Auth-Picker): optionale Referenz auf ein Auth-Profile
   * (UUID). Wenn gesetzt, injiziert der Ingestor die Credentials in den
   * Smoke-Test-Request — so funktioniert "URL testen" auch fuer
   * geschuetzte Provider wie Destatis GENESIS.
   */
  auth_profile_id?: string;
}

export interface SmokeTestSourceResult {
  status: number;
  contentType: string | null;
  preview: string;
  previewBytes: number;
  /**
   * True, wenn der Body groesser war als `previewBytes` und die Vorschau
   * abgeschnitten wurde. Frontend zeigt darauf basierend eine
   * "Vorschau unvollstaendig"-Warnung.
   */
  truncated: boolean;
  durationMs: number;
  finalUrl: string;
}

function toQuery(filters: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '' && v !== null) sp.set(k, String(v));
  }
  const q = sp.toString();
  return q ? `?${q}` : '';
}


// ───── Bundle (MR 3 — Wizard atomic create) ─────────────────────────────

/**
 * Body fuer `POST /api/catalog/datasets/bundle` — die atomare Anlage eines
 * Datasets samt optionaler Source und Extra-Distributionen. Eine Postgres-
 * Transaktion umspannt alle INSERTs; bei Fehler ROLLBACK.
 */
export interface CreateDatasetBundleBody {
  dataset: CreateDatasetBody;
  source?: CreateSourceBody;
  extraDistributions?: CreateDistributionBody[];
}

export interface CreateDatasetBundleResponse {
  dataset: DatasetSummary;
  source: SourceRecord | null;
  distributions: Distribution[];
  silo_table?: { statements: string[] };
}

// ───── Widgets ──────────────────────────────────────────────────────────

export type WidgetType =
  | 'line_chart'
  | 'bar_chart'
  | 'area_chart'
  | 'donut_chart'
  | 'kpi_tile'
  | 'map_marker'
  | 'map_area'
  | 'map_layer'
  | 'link_tile'
  | 'markdown_text'
  | 'data_table'
  | 'section_divider'
  | 'image'
  | 'panel';

export interface WidgetAsset {
  id: string;
  widget_type: WidgetType;
  title: string;
  description: string | null;
  owner: string;
  config: Record<string, unknown>;
  /** Eintrag der Gliederung, dem das gehoert (null: oberste Ebene). */
  organisation_id?: string | null;
  /** Auf der oeffentlichen Seite seines Eintrags der Gliederung zeigen. */
  public?: boolean;
  created_at: string;
  updated_at: string;
}

export interface WidgetListResponse {
  items: WidgetAsset[];
  total: number;
  limit: number;
  offset: number;
}

export interface DataQueryResponse {
  rows: Array<Record<string, unknown>>;
  rowCount: number;
  truncated: boolean;
  elapsedMs: number;
}

/**
 * Conversational Widget-Builder — Multi-Turn-Chat-Response.
 *
 * Wird vom Derivation-Service (Route `POST /api/derivations/chat`) zurueck-
 * gegeben. Die State-Maschine `discovering → confirming → done` lebt
 * server-seitig in der Session; das Frontend rendert nur das jeweils zuletzt
 * berichtete State.
 */
export type ChatSessionState = 'discovering' | 'confirming' | 'refining' | 'done';

export interface WidgetDraft {
  title: string;
  widget_type: WidgetType;
  dataset_slug: string;
  config: Record<string, unknown>;
  summary?: string;
}

export interface ChatTurnRequest {
  sessionId?: string;
  message: string;
  intent?: 'widget' | 'dataset' | 'forecast' | 'derivation' | 'alert';
}

export interface ChatTurnResponse {
  sessionId: string;
  state: ChatSessionState;
  assistant_text: string;
  tool_call?: {
    name: string;
    input: Record<string, unknown>;
  };
  draft?: WidgetDraft;
  preview_data?: {
    ok: boolean;
    rowCount?: number;
    rows?: Array<Record<string, unknown>>;
    error?: string;
  };
  usage: {
    input_tokens: number;
    output_tokens: number;
    cache_read_tokens?: number;
    cache_creation_tokens?: number;
  };
  provider: string;
  model: string;
}

// ───── Builder Live-Previews (MR 3 Multi-Intent-Builder) ───────────────

export interface ForecastPreviewBody {
  dataset_slug: string;
  value_field: string;
  time_field: string;
  method: ForecastMethod;
  /** Stunden — wird im Backend auf 24 h Preview gekuerzt. */
  horizon_hours: number;
  method_params?: Record<string, unknown>;
}

export interface ForecastPreviewResponse {
  history: Array<{ t: string; v: number }>;
  forecast: Array<{ t: string; v: number; lower: number; upper: number }>;
  note?: string;
}

export type AlertPreviewDefinition =
  | {
      kind: 'threshold';
      value_field: string;
      operator: '>' | '>=' | '<' | '<=' | '==' | '!=';
      threshold: number;
      window_minutes?: number;
    }
  | {
      kind: 'trend';
      value_field: string;
      direction: 'up' | 'down';
      delta: number;
      window_minutes: number;
    }
  | {
      kind: 'heartbeat';
      time_field: string;
      max_age_minutes: number;
    };

export interface AlertPreviewBody {
  dataset_slug: string;
  definition: AlertPreviewDefinition;
}

export interface AlertPreviewResponse {
  current_value: number | null;
  last_data_point_at: string | null;
  would_trigger: boolean;
  note?: string;
  age_minutes?: number;
}

export interface DerivationPreviewBody {
  definition: string;
}

export interface DerivationPreviewResponse {
  explain: string[];
}


// ───── Silos (Silos-Uebersichts-Seite) ───────────────────────────────────

/**
 * Silo-Identifier, identisch zu `DatasetSummary['silo']` plus `mqtt` —
 * MQTT-Datasets erscheinen in der Silos-Uebersicht als eigene Karte,
 * werden aber nicht als regulaere Catalog-Datasets ausgewiesen (kein
 * Persistenz-Pfad).
 */
export type SiloKey = 'timescale' | 'postgis' | 'relational' | 'citydb' | 'object' | 'mqtt';

export type SiloIcon = 'database' | 'hard-drive' | 'radio' | 'activity' | 'building';

export type SiloHealth = 'ok' | 'degraded' | 'unavailable';

export interface SiloDataset {
  id: string;
  slug: string;
  title: string;
  silo_location: string;
  row_count: number | null;
  hvd_category: HvdCategory | null;
  lifecycle: 'active' | 'deprecated' | 'archived';
}

export interface SiloOverview {
  silo: SiloKey;
  silo_label: string;
  icon: SiloIcon;
  description: string;
  dataset_count: number;
  /** null fuer object/mqtt; sonst Summe der dataset row_counts. */
  total_row_count: number | null;
  /** Bytes (pg_total_relation_size) — null fuer object/mqtt. */
  storage_size_bytes: number | null;
  health: SiloHealth;
  datasets: SiloDataset[];
}

/** Pruefdienst ohne eigenen Datenbestand (z. B. XPlanValidator der xPlanBox). */
export interface CheckServiceOverview {
  key: string;
  label: string;
  description: string;
  health: SiloHealth;
  version: string | null;
  details: string[];
}

export interface SiloListResponse {
  items: SiloOverview[];
  services?: CheckServiceOverview[];
}


// ───── Providers (Provider-Catalog / Wizard Step 1) ─────────────────────

/**
 * Re-Export der Provider-Registry-Typen aus `@civiport/shared/providers`.
 *
 * Single Source of Truth bleibt der `packages/shared`-Code. Der Re-Export
 * hier ist Convenience: UI-Komponenten importieren weiterhin aus `lib/api.js`
 * und mussen nicht wissen, dass die Typen aus dem shared-Package stammen.
 */
export type {
  AuthType,
  DataProvider,
  ProviderCategory,
  ProviderCoverage,
  ProviderFormat,
} from '@civiport/shared/providers';

/** Filter-Parameter fuer `GET /api/catalog/providers`. */
export interface ProviderListFilters {
  category?: ProviderCategory;
  /** `true` = nur auth-pflichtige Provider, `false` = nur token-freie. */
  auth?: boolean;
  coverage?: ProviderCoverage;
  /** Volltext-Suche ueber name/description/organization/tags/slug. */
  q?: string;
  sort?: 'name' | 'category' | 'auth';
  limit?: number;
  offset?: number;
}

export interface ProviderListResponse {
  items: DataProvider[];
  total: number;
  limit: number;
  offset: number;
}

// ───── Auth-Profile (MR „auth-vault", 3/6 der Provider-Catalog-Serie) ─

/** Auth-Typ eines Profils. Bestimmt das Shape des `credentials`-Bodys. */
export type AuthProfileType =
  | 'api-key'
  | 'http-basic'
  | 'oauth2-client-credentials'
  | 'custom';

/** Credentials-Shape je auth_type (NUR beim Schreiben/Editieren genutzt — die
 *  API gibt credentials nie zurueck). */
export type AuthProfileCredentials =
  | { value: string; header_name?: string; query_param?: string } // api-key
  | { username: string; password: string } // http-basic
  | {
      client_id: string;
      client_secret: string;
      token_endpoint: string;
      scope?: string;
    } // oauth2-client-credentials
  | Record<string, string>; // custom

/** Metadaten-Sicht eines Profils — KEINE credentials, kein Cipherblob. */
export interface AuthProfile {
  id: string;
  title: string;
  provider_slug: string | null;
  auth_type: AuthProfileType;
  owner_sub: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuthProfileCreate {
  title: string;
  provider_slug?: string;
  auth_type: AuthProfileType;
  credentials: AuthProfileCredentials;
  description?: string;
}

export interface AuthProfileUpdate {
  title?: string;
  description?: string | null;
  /** Wenn gesetzt: ueberschreibt den Cipherblob — Shape muss zum bestehenden auth_type passen. */
  credentials?: AuthProfileCredentials;
}

export interface AuthProfileListResponse {
  items: AuthProfile[];
}

export interface AuthProfileDeleteConflictReference {
  source_id: string;
  dataset_id: string;
}


/** POI-Item aus /api/catalog/poi/* — Stations-Stammdatum fuer den
 *  Wizard-PoiPicker. `id` ist die Stationskennung, die der User in die
 *  Source-Config eingibt (DWD: numerisch, Pegel: UUID, UBA: DEBYxxx). */
export interface PoiItem {
  id: string;
  name: string;
  lat: number;
  lon: number;
  meta?: Record<string, string>;
}


// ───── Admin: System & Betrieb (/admin/system) ──────────────────────────

export interface ServiceHealth {
  service: string;
  status: 'ok' | 'degraded' | 'unreachable';
  latency_ms: number | null;
  detail?: unknown;
}

export interface SystemHealthResponse {
  overall: 'ok' | 'degraded' | 'unreachable';
  checked_at: string;
  services: ServiceHealth[];
}

export interface ContainerInfo {
  id: string;
  name: string;
  service: string;
  image: string;
  state: string;
  status: string;
  health: 'healthy' | 'unhealthy' | 'starting' | 'none';
}

/** Neustarts aus „System & Betrieb“: erlaubte Dienste und noch nicht ausgefuehrte Anforderungen. */
export interface SystemRestartState {
  restartable: string[];
  pending: { id: string; service: string; requested_at: string }[];
}

export type SystemContainersResponse =
  | { available: true; checked_at: string; containers: ContainerInfo[] }
  | { available: false; ui_url: string | null; reason: string; message?: string };

export interface LorawanGateway {
  gateway_id: string;
  name: string;
  last_seen_at: string | null;
  online: boolean;
}

export interface LorawanDevice {
  dev_eui: string;
  name: string;
  application: string;
  last_seen_at: string | null;
  disabled: boolean;
}

/** `ui_url`: Adresse der ChirpStack-Oberflaeche, wie der Server sie vorgibt (null = nicht eingerichtet). */
export type LorawanOverviewResponse =
  | {
      available: true;
      ui_url: string | null;
      checked_at: string;
      stats: {
        gateways: number;
        gateways_online: number;
        devices: number;
        applications: number;
        tenants: number;
      };
      gateways: LorawanGateway[];
      devices: LorawanDevice[];
    }
  | { available: false; ui_url: string | null; reason: string; message?: string };

export interface WmsInspection {
  url: string;
  title: string | null;
  /** minzoom/maxzoom: ab/bis wann der Dienst die Ebene zeichnet (aus seinen Massstabsgrenzen). */
  layers: { name: string; title: string; minzoom?: number; maxzoom?: number }[];
  formats: string[];
  constraints: string | null;
}

export interface MapServiceInput {
  url: string;
  layer: string;
  title: string;
  description?: string;
  role: 'grundkarte' | 'ebene';
  anbieter: string;
  license: string;
  attribution: string;
  format?: 'image/png' | 'image/jpeg';
  minzoom?: number;
  maxzoom?: number;
}

export interface GemeindeTreffer {
  ags: string;
  name: string;
  bez: string;
}

export type OrganisationsResponse = Pick<OrganisationsState, 'items' | 'my'>;

/** Verwaltung › Benutzer: Rolle einer Person an einem Eintrag der Gliederung. */
export type MemberRole = 'redaktion' | 'verwaltung';

export interface Member {
  user_sub: string;
  organisation_id: string;
  role: MemberRole;
  name: string | null;
  email: string | null;
  enabled: boolean;
  /** false: Konto gibt es in der Anmeldung nicht mehr. */
  exists: boolean;
  created_by: string | null;
  created_at: string;
  invited_by_me: boolean;
}

export interface MembersResponse {
  items: Member[];
  /** Eintraege, an denen ich Personen zuordnen darf. */
  manageable: string[];
  admin: boolean;
}

export interface PersonHit {
  sub: string;
  name: string;
  email: string | null;
  enabled: boolean;
}

export interface OrganisationUpdate {
  name?: string;
  homepage?: string | null;
  email?: string | null;
  imprint_url?: string | null;
  privacy_url?: string | null;
  kind?: OrganisationKind;
  sort?: number;
  parent_id?: string;
  slug?: string;
}

export interface TenantUpdate {
  name?: string;
  short_name?: string | null;
  homepage?: string | null;
  email?: string | null;
  brand_hue?: number;
  brand_chroma?: number;
  wappen?: string | null;
  logo?: string | null;
  logo_hell?: string | null;
  hero_animation?: boolean;
  embed_origins?: string[];
  modules?: { lorawan?: boolean };
  imprint_url?: string | null;
  privacy_url?: string | null;
}

export const api = {
  listDatasets: (filters: DatasetListFilters = {}) =>
    request<DatasetListResponse>(
      `/api/catalog/datasets${toQuery(filters as Record<string, unknown>)}`,
    ),

  listSilos: () => request<SiloListResponse>('/api/catalog/silos'),

  /** Datei in den Objekt-Silo eines Datensatzes legen (Data-Gateway, hoechstens 100 MiB). */
  putDatasetObject: (datasetId: string, key: string, file: Blob, contentType: string) =>
    request<{ etag: string; size: number }>(
      `/api/data/datasets/${encodeURIComponent(datasetId)}/objects/${key.split('/').map(encodeURIComponent).join('/')}`,
      { method: 'PUT', body: file, headers: { 'Content-Type': contentType } },
    ),

  /** Objekt eines Datensatzes loeschen (Data-Gateway; bei Tabellen nur Originale unter quelle/). */
  deleteDatasetObject: (datasetId: string, key: string) =>
    request<void>(
      `/api/data/datasets/${encodeURIComponent(datasetId)}/objects/${key.split('/').map(encodeURIComponent).join('/')}`,
      { method: 'DELETE' },
    ),

  // ───── Admin: System & Betrieb ─────────────────────────────────────────

  /** Health-Fanout ueber alle internen Services (nur admin). */
  getSystemHealth: () => request<SystemHealthResponse>('/api/system/health'),

  /** Docker-Container-Status via docker.sock (nur admin, optional). */
  getSystemContainers: () =>
    request<SystemContainersResponse>('/api/system/containers'),

  /** Erlaubte und offene Neustarts (nur admin). */
  getSystemRestarts: () => request<SystemRestartState>('/api/catalog/admin/system/restart'),

  /** Neustart eines Dienstes anfordern; module-control fuehrt ihn binnen Sekunden aus. */
  restartService: (service: string) =>
    request<{ id: string; service: string; requested_at: string }>('/api/catalog/admin/system/restart', {
      method: 'POST',
      body: JSON.stringify({ service }),
    }),

  /** LoRaWAN-Uebersicht aus der ChirpStack-DB (nur admin, optional). */
  getLorawanOverview: () =>
    request<LorawanOverviewResponse>('/api/catalog/admin/lorawan/overview'),

  /**
   * Liste der eingebauten Open-Data-Provider (Provider-Catalog).
   *
   * Gefiltert/durchsucht/paginiert ueber `/api/catalog/providers`. Auth ueber
   * das aktuelle Keycloak-Token. Der Wizard Step 1 ruft das mit
   * `{ sort: 'name', limit: 200 }` initial auf und reagiert auf Search-/
   * Filter-Aenderungen mit erneuten Calls.
   */
  listProviders: (filters: ProviderListFilters = {}) => {
    const params: Record<string, unknown> = { ...filters };
    if (filters.auth !== undefined) params.auth = filters.auth ? 'true' : 'false';
    return request<ProviderListResponse>(
      `/api/catalog/providers${toQuery(params)}`,
    );
  },

  /** Einzelner Provider per Slug — Detail-Lookup, fuer spaetere Auth-Profile. */
  getProvider: (slug: string) =>
    request<DataProvider>(
      `/api/catalog/providers/${encodeURIComponent(slug)}`,
    ),

  // ───── Provider-Registry pflegen (Admin) ──────────────────────────────
  createProvider: (body: DataProvider & { slug: string }) =>
    request<DataProvider>('/api/catalog/admin/providers', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateProvider: (slug: string, body: Partial<DataProvider>) =>
    request<DataProvider>(`/api/catalog/admin/providers/${encodeURIComponent(slug)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteProvider: (slug: string) =>
    request<{ ok: true; slug: string }>(
      `/api/catalog/admin/providers/${encodeURIComponent(slug)}`,
      { method: 'DELETE' },
    ),




  // ───── Auth-Profile (Auth-Vault) ────────────────────────────────────────
  /**
   * Liste der eigenen Auth-Profile (Admin sieht alle). Sortiert nach Title.
   */
  listAuthProfiles: () =>
    request<AuthProfileListResponse>('/api/catalog/auth-profiles'),

  /** Einzel-Lookup ohne credentials. */
  getAuthProfile: (id: string) =>
    request<AuthProfile>(`/api/catalog/auth-profiles/${encodeURIComponent(id)}`),

  /**
   * Neues Profil anlegen — Server verschluesselt die credentials und gibt
   * ausschliesslich Metadaten zurueck (KEINE credentials in der Response).
   */
  createAuthProfile: (body: AuthProfileCreate) =>
    request<AuthProfile>('/api/catalog/auth-profiles', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Profil aktualisieren — wenn body.credentials gesetzt ist, wird der
   * Cipherblob ersetzt. Ohne credentials: reine Metadaten-Aktualisierung.
   */
  updateAuthProfile: (id: string, body: AuthProfileUpdate) =>
    request<AuthProfile>(`/api/catalog/auth-profiles/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /**
   * Profil loeschen. 204 No Content bei Erfolg, 409 mit `details.references`
   * wenn das Profil noch von Sources referenziert wird.
   */
  deleteAuthProfile: (id: string) =>
    request<void>(`/api/catalog/auth-profiles/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  // ───── MQTT-Zugaenge (externe Geraete am Broker; Admin) ────────────
  listMqttClients: () => request<MqttClientListResponse>('/api/catalog/admin/mqtt-clients'),
  createMqttClient: (body: { username: string; topic_prefix: string }) =>
    request<MqttClientCreated>('/api/catalog/admin/mqtt-clients', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  rotateMqttClientPassword: (username: string) =>
    request<MqttClientCreated>(`/api/catalog/admin/mqtt-clients/${encodeURIComponent(username)}/rotate`, {
      method: 'POST',
    }),
  deleteMqttClient: (username: string) =>
    request<void>(`/api/catalog/admin/mqtt-clients/${encodeURIComponent(username)}`, {
      method: 'DELETE',
    }),

  getDataset: (id: string) => request<DatasetSummary>(`/api/catalog/datasets/${id}`),

  getDatasetBySlug: (slug: string) =>
    request<DatasetSummary>(`/api/catalog/datasets/by-slug/${slug}`),

  /**
   * Prueft, ob ein Dataset-Slug noch frei ist. Wird vom Wizard im Slug-Input
   * (Step 2) per `onBlur` aufgerufen, damit Kollisionen mit Bootstrap-Seeds
   * (z.B. `wetter-musterdorf`) frueh angezeigt werden statt erst beim Submit.
   *
   * Liefert `true`, wenn der Slug verfuegbar ist. Wirft `ApiError` nur bei
   * Netz-/Auth-Fehlern — "Slug bereits vergeben" ist 200 OK mit `false`.
   */
  checkDatasetSlugAvailable: async (slug: string): Promise<boolean> => {
    const res = await request<{ available: boolean }>(
      `/api/catalog/datasets/slug-available?slug=${encodeURIComponent(slug)}`,
    );
    return res.available;
  },

  createDataset: (body: CreateDatasetBody) =>
    request<DatasetSummary & { silo_table?: { statements: string[] } }>(
      '/api/catalog/datasets',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * Atomare Anlage Dataset + optional Source + optional Extra-Distributionen
   * in einer Postgres-Transaktion. Ersetzt den sequenziellen
   * createDataset → createSource-Pfad im "Neue Quelle anbinden"-Wizard
   * (MR 3 der Wizard-Serie).
   */
  createDatasetBundle: (body: CreateDatasetBundleBody) =>
    request<CreateDatasetBundleResponse>('/api/catalog/datasets/bundle', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateDataset: (id: string, body: UpdateDatasetBody) =>
    request<DatasetSummary>(`/api/catalog/datasets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  archiveDataset: (id: string) =>
    request<void>(`/api/catalog/datasets/${id}`, { method: 'DELETE' }),

  /** Archivierten Datensatz endgueltig loeschen (Admin); 409 mit details.dependents bei Abhaengigkeiten. */
  purgeDataset: (id: string) =>
    request<void>(`/api/catalog/datasets/${id}/permanent`, { method: 'DELETE' }),

  listDistributions: (datasetId: string) =>
    request<{ items: Distribution[] }>(`/api/catalog/datasets/${datasetId}/distributions`),

  createDistribution: (datasetId: string, body: CreateDistributionBody) =>
    request<Distribution>(`/api/catalog/datasets/${datasetId}/distributions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateDistribution: (id: string, body: Partial<CreateDistributionBody>) =>
    request<Distribution>(`/api/catalog/distributions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  deleteDistribution: (id: string) =>
    request<void>(`/api/catalog/distributions/${id}`, { method: 'DELETE' }),

  listSources: (datasetId: string) =>
    request<{ items: SourceRecord[] }>(`/api/catalog/datasets/${datasetId}/sources`),

  createSource: (datasetId: string, body: CreateSourceBody) =>
    request<SourceRecord>(`/api/catalog/datasets/${datasetId}/sources`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateSource: (id: string, body: UpdateSourceBody) =>
    request<SourceRecord>(`/api/catalog/sources/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /**
   * Triggert einen sofortigen Pull der Source ueber den Ingestor-Proxy.
   * Antwort: { status, attempts, rowsFetched, rowsIngested, durationMs } | { error }.
   */
  runSourceJob: (sourceId: string) =>
    request<RunSourceJobResult>(`/api/ingestor/jobs/${sourceId}/run`, {
      method: 'POST',
    }),

  /**
   * Pingt eine externe URL via Ingestor-Proxy an und liefert eine Vorschau
   * (status, contentType, erste ~8 KiB, truncated-Flag). Sicherheit: harter
   * 10-s-Timeout im Backend, Auth ueber das aktuelle User-Token.
   */
  smokeTestSource: (body: SmokeTestSourceBody) =>
    request<SmokeTestSourceResult>('/api/ingestor/smoke-test', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Aggregat-Health-Sicht ueber alle Sources (MR IX/9). */
  sourceHealth: () => request<SourceHealthResponse>('/api/catalog/sources/health'),

  deleteSource: (id: string) =>
    request<void>(`/api/catalog/sources/${id}`, { method: 'DELETE' }),

  listWidgets: (filters: { owner?: string; widget_type?: WidgetType } = {}) =>
    request<WidgetListResponse>(`/api/catalog/widgets${toQuery(filters)}`),

  getWidget: (id: string) => request<WidgetAsset>(`/api/catalog/widgets/${id}`),

  createWidget: (body: {
    widget_type: WidgetType;
    title: string;
    description?: string;
    owner: string;
    config: Record<string, unknown>;
    /** Gliederung: wem das gehoert; ohne Angabe der Arbeitsbereich. */
    organisation_id?: string;
  }) =>
    request<WidgetAsset>('/api/catalog/widgets', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateWidget: (
    id: string,
    body: {
      title?: string;
      description?: string | null;
      config?: Record<string, unknown>;
      public?: boolean;
      /** Umziehen in einen anderen Eintrag der Gliederung. */
      organisation_id?: string;
    },
  ) =>
    request<WidgetAsset>(`/api/catalog/widgets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  deleteWidget: (id: string) =>
    request<void>(`/api/catalog/widgets/${id}`, { method: 'DELETE' }),

  dataQuery: (query: string, parameters?: Array<string | number | boolean | null>) =>
    request<DataQueryResponse>('/api/catalog/data/query', {
      method: 'POST',
      body: JSON.stringify({ query, ...(parameters ? { parameters } : {}) }),
    }),

  // ───── Alerts ──────────────────────────────────────────────────────
  listAlertRules: () => request<{ items: AlertRule[] }>('/api/catalog/alerts/rules'),

  createAlertRule: (body: {
    title: string;
    description?: string;
    owner: string;
    severity?: 'info' | 'warning' | 'critical';
    definition: AlertRuleDefinition;
    enabled?: boolean;
    /** Optionale Liste expliziter E-Mail-Empfaenger fuer den Email-Dispatcher. */
    email_recipients?: string[];
    /** Gliederung: wem das gehoert; ohne Angabe der Arbeitsbereich. */
    organisation_id?: string;
  }) =>
    request<AlertRule>('/api/catalog/alerts/rules', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  deleteAlertRule: (id: string) =>
    request<void>(`/api/catalog/alerts/rules/${id}`, { method: 'DELETE' }),

  listAlertEvents: (params: { state?: 'firing' | 'resolved'; limit?: number } = {}) =>
    request<{ items: AlertEvent[] }>(`/api/catalog/alerts/events${toQuery(params)}`),

  acknowledgeAlertEvent: (id: string, user: string) =>
    request<AlertEvent>(`/api/catalog/alerts/events/${id}/acknowledge`, {
      method: 'POST',
      body: JSON.stringify({ user }),
    }),

  alertStats: () =>
    request<{ total: number; firing: number; unacknowledged: number }>(
      '/api/catalog/alerts/stats',
    ),

  // ───── Derivations ──────────────────────────────────────────────────
  listDerivations: () => request<{ items: Derivation[] }>('/api/catalog/derivations'),

  getDerivation: (id: string) =>
    request<Derivation & { lineage: LineageEdge[] }>(`/api/catalog/derivations/${id}`),

  createDerivation: (body: {
    slug: string;
    title: string;
    description?: string;
    owner: string;
    language: 'sql';
    definition: string;
    materialization?: 'materialized_view' | 'continuous_aggregate' | 'on_demand' | 'scheduled_job';
    register_output_dataset?: boolean;
    /** Gliederung: wem das gehoert; ohne Angabe der Arbeitsbereich. */
    organisation_id?: string;
  }) =>
    request<Derivation & { upstream_datasets: string[]; materialized_rows?: number }>(
      '/api/catalog/derivations',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  refreshDerivation: (id: string) =>
    request<{ rowCount: number; refreshed_at: string }>(
      `/api/catalog/derivations/${id}/refresh`,
      { method: 'POST' },
    ),

  deleteDerivation: (id: string) =>
    request<void>(`/api/catalog/derivations/${id}`, { method: 'DELETE' }),

  /** Vorgaenger eines Datensatzes setzen (Herkunft ohne Ableitung, z. B. Fliesswege aus dem DGM1). */
  setDatasetUpstream: (id: string, datasetIds: string[]) =>
    request<unknown>(`/api/catalog/datasets/${id}/upstream`, { method: 'PUT', body: JSON.stringify({ dataset_ids: datasetIds }) }),

  datasetLineage: (id: string) =>
    request<{ upstream: LineageEdge[]; downstream: LineageEdge[] }>(
      `/api/catalog/datasets/${id}/lineage`,
    ),

  // ───── Audit ───────────────────────────────────────────────────────
  listAuditLog: (
    params: {
      user?: string;
      action?: string;
      asset_type?: string;
      asset_id?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) =>
    request<{ items: AuditEntry[]; total: number; limit: number; offset: number }>(
      `/api/catalog/audit-log${toQuery(params)}`,
    ),

  // ───── Dashboards (Multi-Dashboard, per-User) ────────────────────────
  /**
   * Listet alle Dashboards des aktuellen Users (sortiert nach position).
   * Backend stellt sicher, dass mindestens 1 Default-Dashboard existiert.
   */
  listMyDashboards: () =>
    request<{ items: DashboardSummary[] }>('/api/catalog/dashboards/me'),

  /**
   * Default-Dashboard mit Widgets (Convenience-Wrapper). Auto-Erzeugung bei
   * neuen Usern uebernimmt das Backend.
   */
  getMyDefaultDashboard: () =>
    request<DashboardWithItems>('/api/catalog/dashboards/me/default'),

  /**
   * Detail eines spezifischen Dashboards mit Widgets. `idOrSlug` darf eine
   * UUID oder der Slug sein.
   */
  getMyDashboard: (idOrSlug?: string) => {
    if (idOrSlug === undefined) {
      return request<DashboardWithItems>('/api/catalog/dashboards/me/default');
    }
    return request<DashboardWithItems>(
      `/api/catalog/dashboards/me/${encodeURIComponent(idOrSlug)}`,
    );
  },

  /** Legt ein neues Dashboard fuer den aktuellen User an. */
  createDashboard: (body: { title: string; slug?: string; is_default?: boolean }) =>
    request<DashboardSummary>('/api/catalog/dashboards/me', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Aktualisiert ein Dashboard partial (Titel / Slug / Position / Default-Flag). */
  updateDashboard: (
    id: string,
    body: {
      title?: string;
      slug?: string;
      position?: number;
      is_default?: boolean;
      /** Phase D: Auto-Refresh in Sekunden. 0/30/60/300/900. */
      refresh_interval_seconds?: 0 | 30 | 60 | 300 | 900;
      /** Filterleiste: ersetzt die komplette Filterliste. */
      filters?: DashboardFilterDef[];
      /** Reiter: ersetzt die komplette Reiterliste. */
      tabs?: DashboardTab[];
    },
  ) =>
    request<DashboardSummary>(`/api/catalog/dashboards/me/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /** Loescht ein Dashboard (mind. 1 muss bleiben — 409). */
  deleteDashboard: (id: string) =>
    request<void>(`/api/catalog/dashboards/me/${id}`, { method: 'DELETE' }),

  /**
   * Widget einem Dashboard zuordnen. Wenn `dashboardIdOrSlug` weggelassen
   * wird, wirkt der Call auf das Default-Dashboard (Backwards-Compat).
   */
  addWidgetToDashboard: (
    widgetId: string,
    layout?: { col_start?: number; col_span?: number; tab_id?: string | null },
    dashboardIdOrSlug?: string,
  ) => {
    const path = dashboardIdOrSlug
      ? `/api/catalog/dashboards/me/${encodeURIComponent(dashboardIdOrSlug)}/widgets`
      : '/api/catalog/dashboards/me/widgets';
    return request<{ id: string }>(path, {
      method: 'POST',
      body: JSON.stringify({
        widget_id: widgetId,
        ...(layout?.col_start !== undefined ? { col_start: layout.col_start } : {}),
        ...(layout?.col_span !== undefined ? { col_span: layout.col_span } : {}),
        ...(layout?.tab_id !== undefined ? { tab_id: layout.tab_id } : {}),
      }),
    });
  },

  /**
   * Widget von einem Dashboard entfernen. Ohne `dashboardIdOrSlug` wirkt der
   * Call auf das Default-Dashboard (Backwards-Compat).
   */
  removeWidgetFromDashboard: (widgetId: string, dashboardIdOrSlug?: string) => {
    const path = dashboardIdOrSlug
      ? `/api/catalog/dashboards/me/${encodeURIComponent(dashboardIdOrSlug)}/widgets/${widgetId}`
      : `/api/catalog/dashboards/me/widgets/${widgetId}`;
    return request<void>(path, { method: 'DELETE' });
  },

  /**
   * Phase B (Dashboard-Builder): mass-Update des Layouts. Pro Eintrag entweder
   * `position_id` (die UUID der dashboard_widgets-Zeile) oder die Referenz
   * widget_id/link_tile_id, plus die neuen grid_*-Werte. Catalog schreibt
   * sowohl col_*, row_order als auch grid_* (Backward-Compat).
   */
  updateDashboardLayout: (
    dashboardIdOrSlug: string,
    positions: Array<{
      position_id: string;
      grid_x: number;
      grid_y: number;
      grid_w: number;
      grid_h: number;
      /** Reiter setzen (null = erster Reiter); weggelassen = unveraendert. */
      tab_id?: string | null;
    }>,
  ) =>
    request<{ ok: boolean; updated: number }>(
      `/api/catalog/dashboards/me/${encodeURIComponent(dashboardIdOrSlug)}/layout`,
      {
        method: 'PUT',
        body: JSON.stringify({ positions }),
      },
    ),

  // ───── Forecasts ───────────────────────────────────────────────────
  listForecasts: () => request<{ items: Forecast[] }>('/api/catalog/forecasts'),

  getForecast: (id: string) =>
    request<Forecast & { lineage: LineageEdge[] }>(`/api/catalog/forecasts/${id}`),

  runForecast: (id: string) =>
    request<Forecast>(`/api/catalog/forecasts/${id}/run`, { method: 'POST' }),

  deleteForecast: (id: string) =>
    request<void>(`/api/catalog/forecasts/${id}`, { method: 'DELETE' }),

  /**
   * Legt einen Forecast direkt im Catalog an (ohne LLM-Generator-Pfad).
   * Wird vom Multi-Intent-Builder genutzt, wenn der Chat einen Forecast-
   * Draft finalisiert hat. Body entspricht dem POST /api/catalog/forecasts
   * Schema (siehe services/catalog/src/routes/forecasts.ts).
   */
  createForecast: (body: {
    slug: string;
    title: string;
    description?: string;
    owner: string;
    dataset_slug: string;
    time_field: string;
    target_field: string;
    method: ForecastMethod;
    horizon: number;
    params?: Record<string, unknown>;
    filter?: string;
    training_limit?: number;
    prompt?: string;
    /** Gliederung: wem das gehoert; ohne Angabe der Eintrag des Datensatzes. */
    organisation_id?: string;
  }) =>
    request<Forecast & { source_dataset: { id: string; slug: string } }>(
      '/api/catalog/forecasts',
      { method: 'POST', body: JSON.stringify(body) },
    ),


  /**
   * Conversational Widget-Builder — ein Turn der Multi-Turn-Conversation.
   * Backend speichert die Session in Redis; bei `sessionId=undefined` wird
   * eine neue Session angelegt. Antwort enthaelt assistant_text, ggf. einen
   * tool_call ('ask_clarification' | 'propose_widget' | 'finalize_widget')
   * sowie — sobald ein Vorschlag steht — `draft` + `preview_data`.
   */
  /**
   * Live-Preview: SQL-EXPLAIN fuer einen Derivation-Draft (MR 3 Multi-Intent-Builder).
   * Nutzt die Catalog-Read-Only-Connection mit assertReadOnly-Wall, Timeout 5 s.
   */
  previewDerivation: (body: DerivationPreviewBody) =>
    request<DerivationPreviewResponse>('/api/derivations/derivation/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Live-Preview: Forecast-Mini-Sim (7 Tage Historie + 24 h Forecast) ueber
   * den Sandbox-Forecast-Algorithmus. Nicht-persistierend; ungeeignet fuer
   * Production-Forecasts.
   */
  previewForecast: (body: ForecastPreviewBody) =>
    request<ForecastPreviewResponse>('/api/derivations/forecast/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Live-Preview: "wuerde-jetzt-feuern"-Check fuer einen Alert-Draft.
   * Single-Row-Query gegen das Quell-Dataset; nicht-persistierend.
   */
  previewAlert: (body: AlertPreviewBody) =>
    request<AlertPreviewResponse>('/api/derivations/alert/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  chatTurn: (body: ChatTurnRequest) =>
    request<ChatTurnResponse>('/api/derivations/chat', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ───── Admin: LLM-Konfiguration (MR γ der Chat-Builder-Serie) ─────────
  /**
   * Liest die aktuelle Provider-Auswahl + Modell + API-Key-Status. Nur
   * fuer Realm-Rolle `admin` zugaenglich — andere bekommen 403.
   */
  getLlmAdminConfig: () =>
    request<LlmAdminConfig>('/api/catalog/admin/llm-config'),

  /**
   * Setzt den aktiven Provider (+ optional Modell). Server persistiert in
   * `tenant_settings`; LLM-Service uebernimmt den Wechsel binnen 30 s.
   */
  setLlmAdminConfig: (body: { provider: LlmProvider; model?: string }) =>
    request<LlmAdminConfig & { updated_by: string }>('/api/catalog/admin/llm-config', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /**
   * Verbindungs-Test: schickt einen minimalen Ping an den aktuell aktiven
   * Provider. Antwort enthaelt `ok`, `latency_ms` und ggf. `error`.
   *
   * Geht ueber den Derivation-Service-Proxy, damit das Frontend nicht
   * direkt mit dem internen LLM-Service spricht.
   */
  testLlmConnection: () =>
    request<LlmTestConnectionResult>('/api/derivations/llm/test-connection', {
      method: 'POST',
    }),

  /**
   * Setzt einen LLM-Provider-API-Key in der Catalog-DB. Der Key wird
   * verschluesselt (AES-256-GCM) abgelegt und nie wieder roh zurueckgeliefert
   * — die UI sieht nach dem Speichern nur den Status `set`.
   */
  setLlmApiKey: (body: { provider: KeyedLlmProvider; api_key: string }) =>
    request<{ ok: true; provider: KeyedLlmProvider; source: 'db' }>(
      '/api/catalog/admin/llm-config/api-key',
      {
        method: 'PUT',
        body: JSON.stringify(body),
      },
    ),

  /**
   * Loescht den DB-API-Key. Wenn die env-Variable noch einen Key hat,
   * faellt der Service automatisch darauf zurueck.
   */
  deleteLlmApiKey: (provider: KeyedLlmProvider) =>
    request<{ ok: true; provider: KeyedLlmProvider; source: 'db' | 'env' | 'none' }>(
      `/api/catalog/admin/llm-config/api-key/${provider}`,
      { method: 'DELETE' },
    ),

  // ───── Admin: LLM-Provider-Profile (MR "llm-provider-profiles") ────────
  /** Liste aller Provider-Profile inkl. aktiver ID, Presets und Legacy-Fallback. */
  listLlmProviders: () => request<LlmProviderList>('/api/catalog/admin/llm-providers'),

  /** Legt ein Provider-Profil an; `activate: true` aktiviert es direkt. */
  createLlmProvider: (body: LlmProviderUpsertBody) =>
    request<LlmProviderProfile>('/api/catalog/admin/llm-providers', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Aendert ein Profil. api_key: ''/undefined = unveraendert, null = loeschen. */
  updateLlmProvider: (id: string, body: LlmProviderUpsertBody) =>
    request<LlmProviderProfile>(`/api/catalog/admin/llm-providers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /** Aktiviert ein Profil (genau eines ist aktiv — Server erzwingt das). */
  activateLlmProvider: (id: string) =>
    request<LlmProviderProfile>(`/api/catalog/admin/llm-providers/${id}/activate`, {
      method: 'POST',
    }),

  /** Loescht ein Profil. War es aktiv, greift danach der Legacy-/env-Fallback. */
  deleteLlmProvider: (id: string) =>
    request<{ ok: true; id: string; was_active: boolean }>(
      `/api/catalog/admin/llm-providers/${id}`,
      { method: 'DELETE' },
    ),

  /** Verbindungstest fuer ein bestimmtes Profil (auch inaktive). */
  testLlmProvider: (id: string) =>
    request<LlmTestConnectionProfileResult>('/api/derivations/llm/test-connection', {
      method: 'POST',
      body: JSON.stringify({ provider_id: id }),
    }),

  // ───── Datenqualität: MQA + Link-Check + Suchindex (Phase B2/C/D) ──────
  /** MQA-Katalog-Übersicht (Scores, Durchschnitt, Rating-Verteilung). */
  getMqaOverview: () => request<MqaOverview>('/api/catalog/admin/mqa'),

  /** Berechnet alle MQA-Scores neu (SHACL + Link-Status fliessen ein). */
  recomputeMqa: () =>
    request<MqaRunSummary>('/api/catalog/admin/mqa/recompute', { method: 'POST', body: '{}' }),

  /** Score-Aufschlüsselung eines Datasets. */
  getDatasetQuality: (id: string) =>
    request<DatasetQuality>(`/api/catalog/admin/datasets/${id}/quality`),

  /** SHACL-Konformitätsreport eines Datasets. */
  validateDataset: (id: string) =>
    request<ShaclReport & { dataset_id: string }>(`/api/catalog/admin/datasets/${id}/validate`),

  /** Prüft die Erreichbarkeit aller Distribution-URLs. */
  runLinkCheck: () =>
    request<LinkCheckSummary & { elapsed_ms: number }>('/api/catalog/admin/link-check', {
      method: 'POST',
      body: '{}',
    }),

  /** Baut den OpenSearch-Index neu auf. */
  reindexSearch: () =>
    request<{ indexed: number; elapsed_ms: number }>('/api/catalog/admin/search/reindex', {
      method: 'POST',
      body: '{}',
    }),

  /** Volltext-/Hybrid-Suche über OpenSearch (Facetten + Filter). */
  searchCatalog: (params: {
    q?: string;
    theme?: string;
    format?: string;
    hvd?: string;
    owner?: string;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') qs.set(k, String(v));
    return request<CatalogSearchResult>(`/api/catalog/search?${qs.toString()}`);
  },

  // ───── KI-Metadaten-Copilot ───────────────────────────────────────────
  /** KI schlägt Titel/Beschreibung/Keywords/Themen/HVD für ein Dataset vor. */
  suggestMetadata: (id: string) =>
    request<{ dataset_id: string; suggestion: MetadataSuggestion }>(
      `/api/catalog/admin/datasets/${id}/suggest-metadata`,
      { method: 'POST', body: '{}' },
    ),
  /** Übersetzt Titel/Beschreibung in die gewünschten Sprachen (ISO-Codes). */
  translateMetadata: (id: string, targetLangs: string[]) =>
    request<{ dataset_id: string; translations: Record<string, { title: string; description: string }> }>(
      `/api/catalog/admin/datasets/${id}/translate`,
      { method: 'POST', body: JSON.stringify({ target_langs: targetLangs }) },
    ),
  /** KI nennt konkrete Qualitäts-/Vollständigkeits-Verbesserungen. */
  suggestQuality: (id: string) =>
    request<{ dataset_id: string; score: number | null; suggestions: QualitySuggestion[] }>(
      `/api/catalog/admin/datasets/${id}/suggest-quality`,
      { method: 'POST', body: '{}' },
    ),
  /** Stateless-Copilot für den Anlage-Wizard (Dataset existiert noch nicht). */
  suggestMetadataDraft: (input: {
    title?: string;
    description?: string;
    columns?: Array<{ name: string; type: string; unit?: string; description?: string }>;
  }) =>
    request<{ suggestion: MetadataSuggestion }>('/api/catalog/admin/copilot/suggest-metadata', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // ───── Harvesting (fremde Kataloge einsammeln) ────────────────────────
  listHarvestSources: () =>
    request<{ items: HarvestSource[] }>('/api/catalog/admin/harvest-sources'),
  createHarvestSource: (body: HarvestSourceInput) =>
    request<HarvestSource>('/api/catalog/admin/harvest-sources', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateHarvestSource: (id: string, body: Partial<HarvestSourceInput>) =>
    request<HarvestSource>(`/api/catalog/admin/harvest-sources/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteHarvestSource: (id: string) =>
    request<{ ok: true; id: string }>(`/api/catalog/admin/harvest-sources/${id}`, {
      method: 'DELETE',
    }),
  runHarvestSource: (id: string) =>
    request<HarvestRunResult>(`/api/catalog/admin/harvest-sources/${id}/run`, {
      method: 'POST',
      body: '{}',
    }),

  // ───── Kartendienste (WMS, Verwaltung › Kartendienste) ─────────────────
  inspectMapService: (url: string) =>
    request<WmsInspection>('/api/catalog/admin/map-services/inspect', { method: 'POST', body: JSON.stringify({ url }) }),
  createMapService: (body: MapServiceInput) =>
    request<{ id: string; slug: string; tiles: string }>('/api/catalog/admin/map-services', { method: 'POST', body: JSON.stringify(body) }),
  updateMapService: (id: string, body: { title?: string; attribution?: string; minzoom?: number; maxzoom?: number }) =>
    request<{ id: string; minzoom: number; maxzoom: number }>(`/api/catalog/admin/map-services/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  // ───── Gliederung (Verbund › VG › Gemeinde, Verwaltung › Gliederung) ─────────
  listOrganisations: () => request<OrganisationsResponse>('/api/catalog/organisations'),
  createOrganisation: (body: { parent_id: string; kind: OrganisationKind; name: string; slug?: string; ags?: string }) =>
    request<OrganisationNode>('/api/catalog/admin/organisations', { method: 'POST', body: JSON.stringify(body) }),
  updateOrganisation: (id: string, body: OrganisationUpdate) =>
    request<OrganisationNode>(`/api/catalog/admin/organisations/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteOrganisation: (id: string) => request<void>(`/api/catalog/admin/organisations/${id}`, { method: 'DELETE' }),
  updateOrganisationHero: (id: string, body: { image?: string; credit?: string | null }) =>
    request<OrganisationNode>(`/api/catalog/admin/organisations/${id}/hero`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteOrganisationHero: (id: string) =>
    request<OrganisationNode>(`/api/catalog/admin/organisations/${id}/hero`, { method: 'DELETE' }),
  addOrganisationGebiet: (id: string, ags: string) =>
    request<OrganisationNode & { moved_from: string[] }>(`/api/catalog/admin/organisations/${id}/gebiete`, {
      method: 'POST',
      body: JSON.stringify({ ags }),
    }),
  removeOrganisationGebiet: (id: string, ags: string) =>
    request<OrganisationNode>(`/api/catalog/admin/organisations/${id}/gebiete/${ags}`, { method: 'DELETE' }),

  // ───── Benutzer (Rollen an der Gliederung) ─────────────────────────────
  listMembers: () => request<MembersResponse>('/api/catalog/members'),
  searchPeople: (q: string) => request<{ items: PersonHit[] }>(`/api/catalog/members/search?q=${encodeURIComponent(q)}`),
  setMember: (body: { user_sub: string; organisation_id: string; role: MemberRole }) =>
    request<Member>('/api/catalog/members', { method: 'PUT', body: JSON.stringify(body) }),
  removeMember: (organisationId: string, sub: string) =>
    request<void>(`/api/catalog/members/${organisationId}/${sub}`, { method: 'DELETE' }),
  invitePerson: (body: { first_name: string; last_name: string; email: string; organisation_id: string; role: MemberRole }) =>
    request<{ user_sub: string; name: string; email: string; created: boolean; mailed: boolean }>('/api/catalog/members/invite', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  sendPasswordMail: (sub: string) => request<void>(`/api/catalog/admin/users/${sub}/password-mail`, { method: 'POST' }),
  setTemporaryPassword: (sub: string, password: string) =>
    request<void>(`/api/catalog/admin/users/${sub}/password`, { method: 'PUT', body: JSON.stringify({ password }) }),
  setUserEnabled: (sub: string, enabled: boolean) =>
    request<{ sub: string; enabled: boolean }>(`/api/catalog/admin/users/${sub}/enabled`, { method: 'PUT', body: JSON.stringify({ enabled }) }),

  // ───── Kommune (Darstellung und Gebiet, Verwaltung › Gliederung) ─────────
  updateTenant: (body: TenantUpdate) =>
    request<TenantApi>('/api/catalog/admin/tenant', { method: 'PUT', body: JSON.stringify(body) }),
  /** Titelbild (data:-URL, schon verkleinert) und/oder Bildnachweis setzen. */
  updateTenantHero: (body: { image?: string; credit?: string | null }) =>
    request<TenantApi>('/api/catalog/admin/tenant/hero', { method: 'PUT', body: JSON.stringify(body) }),
  deleteTenantHero: () => request<TenantApi>('/api/catalog/admin/tenant/hero', { method: 'DELETE' }),
  searchGemeinden: (q: string) =>
    request<{ items: GemeindeTreffer[] }>(`/api/catalog/admin/tenant/gemeinden?q=${encodeURIComponent(q)}`),
  addGebiet: (ags: string) =>
    request<TenantApi>('/api/catalog/admin/tenant/gebiete', { method: 'POST', body: JSON.stringify({ ags }) }),
  removeGebiet: (ags: string) =>
    request<TenantApi>(`/api/catalog/admin/tenant/gebiete/${ags}`, { method: 'DELETE' }),

  // ───── Publisher (DCAT dct:publisher) ─────────────────────────────────
  listPublishers: () => request<{ items: Publisher[] }>('/api/catalog/publishers'),
  createPublisher: (body: PublisherInput) =>
    request<Publisher>('/api/catalog/admin/publishers', { method: 'POST', body: JSON.stringify(body) }),
  updatePublisher: (id: string, body: Partial<PublisherInput>) =>
    request<Publisher>(`/api/catalog/admin/publishers/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deletePublisher: (id: string) =>
    request<{ ok: true }>(`/api/catalog/admin/publishers/${id}`, { method: 'DELETE' }),

  // ───── DataServices (DCAT dcat:DataService) ───────────────────────────
  listDataServices: () => request<{ items: DataService[] }>('/api/catalog/admin/data-services'),
  createDataService: (body: DataServiceInput) =>
    request<DataService>('/api/catalog/admin/data-services', { method: 'POST', body: JSON.stringify(body) }),
  updateDataService: (id: string, body: Partial<DataServiceInput>) =>
    request<DataService>(`/api/catalog/admin/data-services/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteDataService: (id: string) =>
    request<{ ok: true }>(`/api/catalog/admin/data-services/${id}`, { method: 'DELETE' }),

  // ───── SPARQL (öffentlicher read-only Endpoint) ───────────────────────
  /** Führt eine read-only SPARQL-Query aus; liefert SPARQL-Results-JSON. */
  sparqlQuery: (query: string) =>
    request<SparqlResults>('/api/catalog/sparql', {
      method: 'POST',
      headers: { 'content-type': 'application/sparql-query', accept: 'application/sparql-results+json' },
      body: query,
    }),

  // ───── Link-Tiles (Admin-verwaltete Link-Kacheln) ─────────────────────
  /**
   * Liste aller Link-Tiles — fuer alle authentifizierten User sichtbar.
   * Bild laedt der Browser separat ueber `linkTileImageUrl(id)`.
   */
  listLinkTiles: () =>
    request<{ items: LinkTile[] }>('/api/catalog/link-tiles'),

  getLinkTile: (id: string) =>
    request<LinkTile>(`/api/catalog/link-tiles/${id}`),

  /**
   * Neue Link-Tile anlegen (Admin only). `image_base64` ist eine Base64-Repr
   * der Bild-Bytes (mit oder ohne `data:`-Prefix); MAX 1 MB roh.
   */
  createLinkTile: (body: LinkTileCreate) =>
    request<LinkTile>('/api/catalog/link-tiles', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateLinkTile: (id: string, body: LinkTileUpdate) =>
    request<LinkTile>(`/api/catalog/link-tiles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  deleteLinkTile: (id: string) =>
    request<void>(`/api/catalog/link-tiles/${id}`, { method: 'DELETE' }),

  /**
   * Pinnt eine Link-Tile auf das angegebene Dashboard (oder Default, wenn
   * ohne slug). Backend akzeptiert `link_tile_id` als Alternative zu
   * `widget_id`.
   */
  addLinkTileToDashboard: (
    linkTileId: string,
    layout?: { col_start?: number; col_span?: number },
    dashboardIdOrSlug?: string,
  ) => {
    const path = dashboardIdOrSlug
      ? `/api/catalog/dashboards/me/${encodeURIComponent(dashboardIdOrSlug)}/widgets`
      : '/api/catalog/dashboards/me/widgets';
    return request<{ id: string }>(path, {
      method: 'POST',
      body: JSON.stringify({
        link_tile_id: linkTileId,
        ...(layout?.col_start !== undefined ? { col_start: layout.col_start } : {}),
        ...(layout?.col_span !== undefined ? { col_span: layout.col_span } : {}),
      }),
    });
  },

  // ───── POI-Search (Wizard PoiPicker) ────────────────────────────────────
  /**
   * Suche Stationen-Stammdaten fuer einen der unterstuetzten Provider.
   * Quelle = `dwd` | `pegel` | `uba`. `q` ist optional — ohne Query liefert
   * der Server die ersten 200 Eintraege.
   */
  searchPois: (source: 'dwd' | 'pegel' | 'uba', q: string) =>
    request<PoiItem[]>(
      `/api/catalog/poi/search?source=${source}&q=${encodeURIComponent(q)}`,
    ),

  /**
   * Listet Stationen (optional gefiltert per Bounding-Box).
   * `bbox` = `lonMin,latMin,lonMax,latMax` in WGS84-Dezimalgrad.
   */
  listPois: (
    source: 'dwd' | 'pegel' | 'uba',
    bbox?: [number, number, number, number],
  ) => {
    const q = bbox ? `&bbox=${bbox.join(',')}` : '';
    return request<PoiItem[]>(`/api/catalog/poi/list?source=${source}${q}`);
  },

};


/** Externer MQTT-Zugang (Benutzer am Broker mit Schreibrecht auf einen Topic-Bereich). */
export interface MqttClient {
  username: string;
  topic_prefix: string;
  other_roles: string[];
  disabled: boolean;
}
export interface MqttBrokerInfo {
  host: string;
  port: number;
  tls: boolean;
}
export interface MqttClientListResponse {
  configured: boolean;
  broker: MqttBrokerInfo | null;
  items: MqttClient[];
}
export interface MqttClientCreated {
  username: string;
  topic_prefix?: string;
  /** Wird nur einmal angezeigt. */
  password: string;
  broker: MqttBrokerInfo | null;
}

export interface DashboardItem {
  position_id: string;
  /** Legacy: 1-indexed Spaltenanfang (1..12). */
  col_start: number;
  /** Legacy: Spaltenbreite (1..12). */
  col_span: number;
  /** Legacy: Reihenposition. */
  row_order: number;
  /** Phase B: react-grid-layout x (0-indexed). */
  grid_x: number;
  /** Phase B: react-grid-layout y (0-indexed). */
  grid_y: number;
  /** Phase B: react-grid-layout w (1..12). */
  grid_w: number;
  /** Phase B: react-grid-layout h (>=1). */
  grid_h: number;
  /** Reiter (DashboardTab.id); null = erster Reiter. */
  tab_id: string | null;
  added_at: string;
  widget: WidgetAsset;
}

/**
 * Eintrag der Dashboard-Liste — eine Zeile pro Dashboard des Users mit
 * Aggregaten (widget_count). Reihenfolge ist (position ASC, created_at ASC).
 */
export interface DashboardSummary {
  id: string;
  slug: string;
  title: string;
  is_default: boolean;
  position: number;
  /** Phase D: Auto-Refresh-Intervall in Sekunden (0 = aus). */
  refresh_interval_seconds: number;
  widget_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * Detail-Antwort eines Dashboards: Metadaten + alle Widgets in Render-Form.
 */
/**
 * Filterdefinition der Dashboard-Filterleiste (pro Dashboard gespeichert).
 * `column` ist der Spaltenname in den Widget-Abfragen; der Filter greift bei
 * allen Widgets, deren Query diese Spalte liefert (Heuristik wie beim Zeitfilter).
 */
export interface DashboardFilterDef {
  id: string;
  label: string;
  column: string;
  type: 'select' | 'text';
  /** Mehrfachauswahl (nur select). */
  multi?: boolean;
  /** Feste Werteliste (select). */
  options?: string[];
  /** SQL-Abfrage, deren erste Spalte die Werte liefert (select). */
  options_query?: string;
}

/** Reiter innerhalb eines Dashboards. Der erste Reiter nimmt Kacheln ohne tab_id auf. */
export interface DashboardTab {
  id: string;
  label: string;
}

export interface DashboardWithItems {
  dashboard_id: string;
  slug: string;
  title: string;
  is_default: boolean;
  /** Phase D: Auto-Refresh-Intervall in Sekunden (0 = aus). */
  refresh_interval_seconds: number;
  filters: DashboardFilterDef[];
  tabs: DashboardTab[];
  items: DashboardItem[];
}

export interface AuditEntry {
  id: string;
  occurred_at: string;
  user_id: string;
  user_ip: string | null;
  action: string;
  asset_type: string | null;
  asset_id: string | null;
  prompt: string | null;
  catalog_context: Record<string, unknown> | null;
  generated_output: string | null;
  validation_result: Record<string, unknown> | null;
  execution_result: Record<string, unknown> | null;
  result_hash: string | null;
}

export interface Derivation {
  id: string;
  /** Eintrag der Gliederung, dem das gehoert (null: oberste Ebene). */
  organisation_id?: string | null;
  title: string;
  description: string | null;
  owner: string;
  language: 'sql' | 'js';
  definition: string;
  materialization: 'materialized_view' | 'continuous_aggregate' | 'on_demand' | 'scheduled_job';
  materialized_view_name: string | null;
  last_refreshed_at: string | null;
  last_refresh_rowcount: { rowCount: number } | null;
  output_dataset_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface LineageEdge {
  id: string;
  upstream_dataset_id: string;
  downstream_dataset_id: string;
  derivation_id: string | null;
  /** Titel des verknuepften Datensatzes (Vorgaenger bzw. Nachfolger). */
  upstream_title?: string | null;
  downstream_title?: string | null;
  created_at: string;
}

// ───── Alert-Types ───────────────────────────────────────────────────────

export interface AlertRule {
  id: string;
  /** Eintrag der Gliederung, dem das gehoert (null: oberste Ebene). */
  organisation_id?: string | null;
  title: string;
  description: string | null;
  owner: string;
  severity: 'info' | 'warning' | 'critical';
  definition: AlertRuleDefinition;
  enabled: boolean;
  /** Optionale Liste expliziter E-Mail-Empfaenger (Migration 0018). */
  email_recipients?: string[];
  last_evaluated_at: string | null;
  last_state: string;
  created_at: string;
  updated_at: string;
}

export type AlertRuleDefinition =
  | {
      type: 'threshold';
      dataset_id: string;
      field: string;
      operator: '>' | '>=' | '<' | '<=' | '==' | '!=';
      value: number;
      window_seconds?: number;
    }
  | {
      type: 'trend';
      dataset_id: string;
      field: string;
      delta_percent: number;
      window_seconds: number;
    }
  | {
      type: 'heartbeat';
      dataset_id: string;
      max_silence_seconds: number;
    }
  | {
      type: 'source-health';
      health_threshold: 'error' | 'stale' | 'error_or_stale';
      source_id?: string;
    };

export interface AlertEvent {
  id: string;
  rule_id: string;
  fired_at: string;
  severity: 'info' | 'warning' | 'critical';
  state: 'firing' | 'resolved';
  message: string;
  payload: Record<string, unknown> | null;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
}

export type ForecastMethod = 'sma' | 'linear' | 'holt-winters';

export interface ForecastPrediction {
  step: number;
  point: number;
  lower: number;
  upper: number;
}

export interface Forecast {
  id: string;
  /** Eintrag der Gliederung, dem das gehoert (null: oberste Ebene). */
  organisation_id?: string | null;
  slug: string;
  title: string;
  description: string | null;
  owner: string;
  dataset_id: string;
  time_field: string;
  target_field: string;
  method: ForecastMethod;
  params: Record<string, unknown>;
  horizon: number;
  training_window: {
    size: number;
    first: string | null;
    last: string | null;
    limit: number;
  } | null;
  predictions: ForecastPrediction[] | null;
  diagnostics: Record<string, unknown> | null;
  prompt_history: Array<{ at: string; prompt: string }>;
  last_trained_at: string | null;
  created_at: string;
  updated_at: string;
}

// ───── Source-Health (MR IX/9) ──────────────────────────────────────────

export type SourceHealthClass = 'ok' | 'stale' | 'error';

export interface SourceHealthItem {
  source_id: string;
  dataset_id: string;
  dataset_slug: string;
  type: string;
  strategy: string;
  schedule_cron: string | null;
  status: string;
  last_run_at: string | null;
  last_error: string | null;
  expected_interval_seconds: number | null;
  seconds_since_last_run: number | null;
  health: SourceHealthClass;
}

export interface SourceHealthAggregate {
  total: number;
  ok: number;
  stale: number;
  error: number;
  worst: SourceHealthClass;
}

export interface SourceHealthResponse {
  items: SourceHealthItem[];
  aggregate: SourceHealthAggregate;
}

export interface SourceRecord {
  id: string;
  dataset_id: string;
  type: SourceType;
  flow_id: string;
  strategy: SourceStrategy;
  schedule_cron: string | null;
  config: Record<string, unknown>;
  /** `running`: lang laufender Job ist gerade aktiv. */
  status: 'ok' | 'warning' | 'error' | 'inactive' | 'running';
  last_run_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}


// ───── Link-Tiles (Admin-Kacheln) ──────────────────────────────────────

export interface LinkTile {
  id: string;
  title: string;
  subtitle: string | null;
  url: string;
  image_mime: string;
  open_in_tab: boolean;
  position: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface LinkTileCreate {
  title: string;
  subtitle?: string;
  url: string;
  image_base64: string;
  image_mime: 'image/png' | 'image/jpeg' | 'image/jpg' | 'image/webp' | 'image/svg+xml' | 'image/gif';
  open_in_tab?: boolean;
  position?: number;
}

export interface LinkTileUpdate {
  title?: string;
  subtitle?: string | null;
  url?: string;
  image_base64?: string;
  image_mime?: 'image/png' | 'image/jpeg' | 'image/jpg' | 'image/webp' | 'image/svg+xml' | 'image/gif';
  open_in_tab?: boolean;
  position?: number;
}

/**
 * Hilfsfunktion: spiegelt eine Link-Tile als `WidgetAsset`-kompatibles
 * Objekt, damit dieselbe `WidgetRenderer`-Komponente sie in der Galerie
 * neben regulaeren Widgets rendern kann.
 */
export function linkTileAsWidget(tile: LinkTile, apiBaseUrl = ''): WidgetAsset {
  return {
    id: tile.id,
    widget_type: 'link_tile',
    title: tile.title,
    description: tile.subtitle,
    owner: tile.created_by,
    config: {
      title: tile.title,
      subtitle: tile.subtitle ?? undefined,
      url: tile.url,
      image_url: `${apiBaseUrl}/api/catalog/link-tiles/${tile.id}/image`,
      image_mime: tile.image_mime,
      open_in_tab: tile.open_in_tab,
      data_source: { type: 'static', rows: [] },
    },
    created_at: tile.created_at,
    updated_at: tile.updated_at,
  };
}

// ───── Admin: LLM-Konfiguration — Types ─────────────────────────────────

export type LlmProvider = 'anthropic' | 'openai' | 'ollama';

/** Status des API-Key in der .env des LLM-Service. */
export type ApiKeyStatus = 'set' | 'unset' | 'n/a';

/** Keyed Provider — die einzigen, fuer die ein API-Key in der DB persistiert wird. */
export type KeyedLlmProvider = 'anthropic' | 'openai';

export interface ApiKeyInfo {
  /** 'set' wenn entweder DB- oder env-Wert vorhanden ist. */
  status: 'set' | 'unset';
  /** Quelle des aktuell aktiven Keys. */
  source: 'db' | 'env' | 'none';
}

export interface LlmAdminConfig {
  provider: LlmProvider;
  model: string;
  /** Status fuer den aktuell aktiven Provider (kompatibilitaetsfeld). */
  api_key_status: ApiKeyStatus;
  /** Detail-Status pro Keyed-Provider — Status + Quelle. */
  api_keys: Record<KeyedLlmProvider, ApiKeyInfo>;
  /** True wenn der Catalog-Service einen Master-Encryption-Key hat. */
  encryption_configured: boolean;
  providers: readonly LlmProvider[];
  default_models: Record<LlmProvider, string>;
}

export interface LlmTestConnectionResult {
  ok: boolean;
  provider: LlmProvider;
  model: string;
  latency_ms: number;
  reply?: string;
  error?: string;
}

// ───── Admin: LLM-Provider-Profile (MR "llm-provider-profiles") ─────────

/**
 * Provider-Typen der Profil-Verwaltung. `openai_compatible` ist der
 * Sammel-Typ fuer alles, was die OpenAI-API spricht — inkl. Ollama (dessen
 * /v1-Endpoint), vLLM, LM Studio, Azure OpenAI.
 */
export type LlmProviderType = 'anthropic' | 'openai' | 'openai_compatible';

export interface LlmProviderProfile {
  id: string;
  name: string;
  provider_type: LlmProviderType;
  base_url: string | null;
  model: string;
  /** Key wird nie im Klartext geliefert — nur Status + Maske (first4…last4). */
  api_key_set: boolean;
  api_key_masked: string | null;
  is_active: boolean;
  updated_at: string;
  updated_by: string | null;
}

export interface LlmProviderList {
  items: LlmProviderProfile[];
  active_id: string | null;
  encryption_configured: boolean;
  /** Nur gesetzt, solange KEIN Profil aktiv ist (Legacy-tenant_settings/env). */
  legacy_fallback: { provider: string; model: string; source: string } | null;
  model_presets: Record<LlmProviderType, string[]>;
}

export interface LlmProviderUpsertBody {
  name?: string;
  provider_type?: LlmProviderType;
  base_url?: string | null;
  model?: string;
  /** ''/undefined = unveraendert (PUT) bzw. keiner (POST); null = loeschen. */
  api_key?: string | null;
  activate?: boolean;
}

export interface LlmTestConnectionProfileResult {
  ok: boolean;
  provider: string;
  model: string;
  latency_ms: number;
  reply?: string;
  error?: string;
}

// ───── Datenqualität: MQA / SHACL / Link-Check / Suche (Phase B2/C/D) ────
export type MqaRating = 'excellent' | 'good' | 'sufficient' | 'bad';
export interface DimensionScore {
  score: number;
  max: number;
}
export interface MqaDatasetRow {
  dataset_id: string;
  title: string;
  slug: string;
  score: number;
  max_score: number;
  rating: MqaRating;
  dimensions: Record<string, DimensionScore>;
  computed_at: string;
}
export interface MqaOverview {
  count: number;
  average: number;
  max_score: number;
  ratings: Record<MqaRating, number>;
  items: MqaDatasetRow[];
}
export interface MqaRunSummary {
  datasets: number;
  average: number;
  ratings: Record<MqaRating, number>;
  elapsed_ms: number;
}
export interface DatasetQuality {
  dataset_id: string;
  score: number;
  max_score: number;
  rating: MqaRating;
  dimensions: Record<string, DimensionScore>;
  computed_at: string;
}
export interface ShaclResult {
  severity: 'Violation' | 'Warning' | 'Info';
  message: string;
  focusNode: string | null;
  path: string | null;
}
export interface ShaclReport {
  conforms: boolean;
  violations: number;
  warnings: number;
  results: ShaclResult[];
}
export interface LinkCheckSummary {
  checked: number;
  ok: number;
  broken: number;
  unknown: number;
}
export interface CatalogSearchResult {
  total: number;
  mode: 'hybrid' | 'bm25' | 'match_all';
  items: Array<{ id: string; score: number; title: string; slug: string; description: string; themes: string[]; formats: string[]; owner: string }>;
  facets: Record<string, Array<{ key: string; doc_count: number }>>;
}

// ───── KI-Metadaten-Copilot ─────
export interface MetadataSuggestion {
  title: string;
  description: string;
  keywords: string[];
  themes: string[];
  hvd_category: string | null;
}
export interface QualitySuggestion {
  field: string;
  issue: string;
  recommendation: string;
  example?: string;
}

// ───── Harvesting ─────
export type HarvestType = 'ckan' | 'dcatap-rdf';
export interface HarvestSource {
  id: string;
  name: string;
  type: HarvestType;
  url: string;
  enabled: boolean;
  /** Dateien in die eigenen Speicher übernehmen (Standard) statt nur zu verlinken. */
  copy_data: boolean;
  /** Im eigenen Katalog veröffentlichen (nur bei Lizenz mit Weitergabe); sonst nur eigene Nutzung. */
  publish: boolean;
  /** Obergrenze für kopierte Dateien, in MB. */
  max_copy_mb: number;
  schedule_cron: string | null;
  filter_query: string | null;
  max_datasets: number | null;
  last_run_at: string | null;
  last_status: string | null;
  last_error: string | null;
  datasets_count: number;
  created_at: string;
  updated_at: string;
}
export interface HarvestSourceInput {
  name: string;
  type: HarvestType;
  url: string;
  enabled?: boolean;
  copy_data?: boolean;
  publish?: boolean;
  max_copy_mb?: number;
  schedule_cron?: string | null;
  filter_query?: string;
  max_datasets?: number;
}
export interface HarvestRunResult {
  imported: number;
  errors: number;
  elapsed_ms: number;
  /** Datensätze, deren Dateien in die eigenen Speicher übernommen werden. */
  copied: number;
  /** Speicher-Obergrenze erreicht: in diesem Lauf keine neuen Kopien. */
  storage_full?: boolean;
}

// ───── Publisher / DataServices (DCAT) ─────
export interface Publisher {
  id: string;
  name: string;
  homepage: string | null;
  email: string | null;
  type: string | null;
  identifier?: string | null;
  /** Die eigene Kommune (genau eine, nicht löschbar). */
  is_own: boolean;
  /** Anzahl der Datensätze dieses Herausgebers. */
  datasets?: number;
  created_at?: string;
  updated_at?: string;
}
export interface PublisherInput {
  name: string;
  homepage?: string;
  email?: string;
  type?: string;
  identifier?: string;
}
export type DataServiceType = 'wfs' | 'wms' | 'rest' | 'sparql' | 'ogcapi' | 'other';
export interface DataService {
  id: string;
  title: string;
  description: string | null;
  endpoint_url: string;
  endpoint_description: string | null;
  serves_dataset_ids: string[];
  conforms_to: string[];
  license: string | null;
  access_rights: 'PUBLIC' | 'RESTRICTED' | 'NON_PUBLIC' | null;
  service_type: DataServiceType | null;
  /** Eigene CiviPort-Schnittstelle: Endpunkt und bediente Datensaetze pflegt CiviPort selbst. */
  builtin_key: string | null;
  created_at: string;
  updated_at: string;
}
export interface DataServiceInput {
  title: string;
  description?: string;
  endpoint_url: string;
  endpoint_description?: string;
  serves_dataset_ids?: string[];
  conforms_to?: string[];
  license?: string;
  access_rights?: 'PUBLIC' | 'RESTRICTED' | 'NON_PUBLIC';
  service_type?: DataServiceType;
}

// ───── SPARQL ─────
export interface SparqlResults {
  head: { vars?: string[]; link?: string[] };
  results?: { bindings: Array<Record<string, { type: string; value: string; datatype?: string; 'xml:lang'?: string }>> };
  boolean?: boolean;
}
